import {createSelector} from "reselect";
import {filterHierarchy} from 'components/layouts/Taxes/TaxesPageUtility';
import {ADVISORY, isPershingProduct, RETAIL, SMALL_BUSINESS_RETIREMENT, isActiveAdvisoryOrActivePlus} from "services/Products";
import {isIRA} from 'services/AccountTypes';
import { hasMutualFunds } from "state/view/ViewSelectors";

export const getTaxonomyHierarchy = (state) => state.cdsPortfolio.taxonomyHierarchy;

export const getTaxonomyGroups = (state) => state.cdsPortfolio.taxonomyGroups;

export const getAgreementSummaries = (state) => state.cdsPortfolio.agreementSummaries;

export const hasSmallBusinessRetirementCategory = createSelector(
    getTaxonomyGroups,
    (taxonomyGroups) => {
        return taxonomyGroups?.some(group => group.tier === 'CATEGORY' && group.type === SMALL_BUSINESS_RETIREMENT);
    }
);

export const getSmallBusinessRetirementAgreements = createSelector(
    getTaxonomyGroups,
    getAgreementSummaries,
    (taxonomyGroups, agreementSummaries) => {
        if (!taxonomyGroups || !agreementSummaries) {
            return [];
        }

        const categoryGroups = taxonomyGroups?.find(group => group.tier === 'CATEGORY' && group.type === SMALL_BUSINESS_RETIREMENT);
        return categoryGroups?.agreements.map(agreement => {
            return agreementSummaries.find(summary => summary.uniqueId === agreement);
        });
    }
);

export const getPershingAccounts = createSelector(
    getTaxonomyHierarchy,
    getTaxonomyGroups,
    getAgreementSummaries,
    (taxonomyHierarchy, taxonomyGroups, summary) => {
    const pershingAccounts = [];
    if (taxonomyHierarchy && taxonomyGroups) {     
        taxonomyGroups.map(group => { 
            if (group.tier === 'CATEGORY' && group.type === ADVISORY) { 
                group.childGroups.map(childGroupId => {
                    const accountGroup = taxonomyGroups.find(accGroup => accGroup.id === childGroupId && accGroup.tier === 'ACCOUNT_GROUP');
                    const isIra = isIRA(Number(accountGroup.id.split('-')?.[1]));
                    if (isIra) {
                        accountGroup.agreements.map(agreement => {
                            summary = summary?.filter(summaryAgreement => {
                                return  summaryAgreement.uniqueId !== agreement || (summaryAgreement.uniqueId === agreement &&
                                !isActiveAdvisoryOrActivePlus(summaryAgreement.agreementKey.productType?.enumerationID));
                            });
                        });
                    }
                });
            }                 
        });         
    }
    summary?.forEach(agreement=> {
        const {
          agreementKey: { productType, agreementID }
        } = agreement;

        if (isPershingProduct(productType?.enumerationID)) {
            if (!pershingAccounts.find(account => account.enumerationID === productType?.enumerationID)) {
                pershingAccounts.push({
                  agreementID,
                  accountDisplayName: agreement.agreementName,
                  isViewOnly: agreement.isViewOnly,
                  ...productType
                });
            }
        }
    })
   return  pershingAccounts.sort((a,b)=>a.accountDisplayName.localeCompare(b.accountDisplayName));
});

export const getTaxonomyDataV2 = createSelector(
    getTaxonomyHierarchy,
    getTaxonomyGroups,
    getAgreementSummaries,
    (taxonomyHierarchy, taxonomyGroups, agreementSummaries) => {
        // if empty response, return empty array
        if (!taxonomyHierarchy || !taxonomyGroups || !agreementSummaries) {
            return [];
        }

        // filter out to only include category types of Retail or Advisory
        const filteredHierarchy = filterHierarchy(taxonomyHierarchy, taxonomyGroups);

        // if only not Retail or Advisory, return empty array
        if (filteredHierarchy.length === 0) {
            return [];
        }

        // map through taxonomyHierarchy to see how many tables there will be
        return filteredHierarchy.map(hierarchy => {
            //  for each table map the category header
            const categoryGroup = taxonomyGroups.find(group => group.tier === 'CATEGORY' && group.id === hierarchy.id);

            // map account types to category header - through childGroups
            let accountGroups = categoryGroup.childGroups.map(childGroupId => {
                const accountGroup = taxonomyGroups.find(accGroup => accGroup.id === childGroupId && accGroup.tier === 'ACCOUNT_GROUP');

                // map corresponding agreements to account group
                const agreements = accountGroup.agreements.map(agreement => {
                    // find list of agreements
                    const agreementSummary = agreementSummaries.find(summary => summary.uniqueId === agreement);
                    const {
                        agreementKey,
                        agreementName,
                        agreementNumberDisplay,
                        isTaxable,
                        positions,
                        retirement,
                        sweep,
                        uniqueId,
                        isViewOnly
                    } = agreementSummary;
                    return {
                        agreementKey: agreementKey,
                        agreementName: agreementName,
                        agreementNumberDisplay: agreementNumberDisplay,
                        isTaxable: isTaxable,
                        positions: positions,
                        retirement: retirement,
                        sweep: sweep,
                        uniqueId: uniqueId,
                        isViewOnly: isViewOnly
                    };
                });

                // for every account type show the agreements listed under it
                const {id, name, tier, type} = accountGroup
                return {
                    agreements: agreements,
                    id: id,
                    name: name,
                    tier: tier,
                    type: type
                };
            });

            if (categoryGroup.type === SMALL_BUSINESS_RETIREMENT) {
                accountGroups = accountGroups.filter(accountGroup => accountGroup.type === RETAIL || accountGroup.type === ADVISORY);
            }        

            // for every hierarchy, show the category and account type
            const {id, name, tier, type} = categoryGroup;
            return {
                id: id,
                name: name,
                tier: tier,
                type: type,
                accountGroup: accountGroups
            };
        });
    }
);



export const hasOnlyViewOnlyPerishingAccounts = createSelector(
  hasMutualFunds,
  getPershingAccounts,
  (mutualFundExist, perishingAccounts) => {
    if (mutualFundExist) {
      return false;
    }

    if (!perishingAccounts || perishingAccounts.length === 0) {
      return false;
    }
    const isViewOnly = perishingAccounts.every((account) => account.isViewOnly);
    return isViewOnly;

   
  }
);